import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search, X, Loader2, ChevronLeft, ChevronDown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Slider } from "@/components/ui/slider";
import {
  searchShopifyProductsWithRelated,
  searchShopifyCollections,
  ShopifyProduct,
  ShopifyCollection,
  SearchResult,
} from "@/lib/shopify";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { formatPrice } from "@/lib/utils";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";

export const SearchDialog = () => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [categoryResults, setCategoryResults] = useState<ShopifyCollection[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRequestIdRef = useRef(0);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Use global preloaded data
  const { 
    isLoaded, 
    isLoading, 
    searchSettings, 
    defaultProducts, 
    allProducts,
    allCollections,
    preload 
  } = useGlobalPreload();

  // Ensure data is preloaded when component mounts
  useEffect(() => {
    preload();
  }, [preload]);

  // Get max price from all products for the slider
  const maxPrice = useMemo(() => {
    const prices = allProducts.map(p => parseFloat(p.priceRange?.minVariantPrice?.amount || "0"));
    return Math.max(...prices, 5000);
  }, [allProducts]);

  // Initialize price range when max price is calculated
  useEffect(() => {
    if (maxPrice > 0) {
      setPriceRange([0, Math.ceil(maxPrice)]);
    }
  }, [maxPrice]);

  // Get unique categories from collections
  const availableCategories = useMemo(() => {
    return allCollections.map(c => ({
      handle: c.node.handle,
      title: c.node.title,
      image: c.node.image?.url
    }));
  }, [allCollections]);

  const doSearch = useCallback(async (q: string) => {
    const requestId = ++searchRequestIdRef.current;
    setIsSearching(true);
    setHasSearched(false);

    try {
      const [result, collections] = await Promise.all([
        searchShopifyProductsWithRelated(q, 40),
        searchSettings.show_categories ? searchShopifyCollections(q) : Promise.resolve([]),
      ]);

      if (searchRequestIdRef.current !== requestId) return;

      setSearchResult(result);
      setCategoryResults(collections);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching:", error);
      if (searchRequestIdRef.current === requestId) {
        setSearchResult(null);
        setCategoryResults([]);
        setHasSearched(true);
      }
    } finally {
      if (searchRequestIdRef.current === requestId) {
        setIsSearching(false);
      }
    }
  }, [searchSettings.show_categories]);

  // Update query immediately; real searching is debounced in an effect
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      setIsSearching(true); // Start loading immediately when typing
      setHasSearched(false);
    }
  }, []);

  // Debounced remote search (covers ALL products/collections, not a prefetched subset)
  useEffect(() => {
    if (!open) return;

    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResult(null);
      setCategoryResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void doSearch(q);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, searchQuery, doSearch]);

  const handleClear = () => {
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setHasSearched(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setIsSearching(false);
    setHasSearched(false);
    setShowFilters(false);
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
  };

  const hasActiveFilters = selectedCategory !== null || priceRange[0] > 0 || priceRange[1] < Math.ceil(maxPrice);

  // Get exact matches and related products
  const exactMatches = searchResult?.exactMatches || [];
  const relatedProducts = searchResult?.relatedProducts || [];
  const fallbackTerm = searchResult?.fallbackTerm;
  const searchTerms = searchResult?.searchTermsUsed || [];

  // Products to display when not searching - use preloaded data, then apply filters
  const displayProducts = useMemo(() => {
    let products: ShopifyProduct[];
    
    if (searchQuery.trim().length >= 2) {
      // Combine exact matches with related (exact first)
      products = [...exactMatches, ...relatedProducts.filter(p => !exactMatches.some(e => e.id === p.id))];
    } else {
      products = defaultProducts.length > 0 ? defaultProducts : allProducts.slice(0, 12);
    }

    // Apply price filter
    products = products.filter(product => {
      const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
      return price >= priceRange[0] && price <= priceRange[1];
    });

    return products;
  }, [searchQuery, exactMatches, relatedProducts, defaultProducts, allProducts, priceRange]);

  // Separate exact and related for display purposes
  const displayExact = useMemo(() => {
    return exactMatches.filter(product => {
      const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
      return price >= priceRange[0] && price <= priceRange[1];
    });
  }, [exactMatches, priceRange]);

  const displayRelated = useMemo(() => {
    return relatedProducts
      .filter(p => !exactMatches.some(e => e.id === p.id))
      .filter(product => {
        const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
        return price >= priceRange[0] && price <= priceRange[1];
      });
  }, [relatedProducts, exactMatches, priceRange]);

  const hasResults = searchQuery.trim().length >= 2;
  const showCategories = hasResults && categoryResults.length > 0 && searchSettings.show_categories;
  const showRelatedSection = hasResults && hasSearched && !isSearching && displayExact.length < 4 && displayRelated.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) {
        setSearchQuery("");
        setSearchResult(null);
        setCategoryResults([]);
        setIsSearching(false);
        setHasSearched(false);
        setShowFilters(false);
        setSelectedCategory(null);
        setPriceRange([0, Math.ceil(maxPrice)]);
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="hover:bg-transparent" aria-label="חיפוש מוצרים">
          <Search className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6" dir="rtl">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-right text-lg sm:text-xl font-light">חיפוש מוצרים</DialogTitle>
        </DialogHeader>
        
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute right-3 top-3 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="חפש תכשיטים לפי שם..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pr-10 pl-20 text-right h-12 text-base"
            autoFocus
          />
          <div className="absolute left-2 top-2 flex items-center gap-1">
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleClear}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${hasActiveFilters ? 'text-primary' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <Collapsible open={showFilters} onOpenChange={setShowFilters}>
          <CollapsibleContent className="space-y-4 pt-3 pb-2">
            <div className="bg-muted/30 rounded-lg p-3 sm:p-4 space-y-4">
              {/* Price Range Filter */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">טווח מחירים</span>
                  <span className="text-xs text-muted-foreground">
                    ₪{priceRange[0].toLocaleString()} - ₪{priceRange[1].toLocaleString()}
                  </span>
                </div>
                <Slider
                  value={priceRange}
                  onValueChange={(value) => setPriceRange(value as [number, number])}
                  min={0}
                  max={Math.ceil(maxPrice)}
                  step={50}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>₪0</span>
                  <span>₪{Math.ceil(maxPrice).toLocaleString()}</span>
                </div>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <span className="text-sm font-medium block">קטגוריה</span>
                <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                      selectedCategory === null
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/50'
                    }`}
                  >
                    הכל
                  </button>
                  {availableCategories.slice(0, 8).map((cat) => (
                    <button
                      key={cat.handle}
                      onClick={() => setSelectedCategory(cat.handle)}
                      className={`px-3 py-1.5 rounded-full text-xs sm:text-sm border transition-all ${
                        selectedCategory === cat.handle
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border hover:border-primary/50'
                      }`}
                    >
                      {cat.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="w-full sm:w-auto"
                >
                  נקה סינון
                </Button>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Results count - only show when search completed */}
        {hasResults && hasSearched && !isSearching && (
          <div className="text-sm text-muted-foreground text-right flex items-center gap-2 py-1">
            <span>נמצאו {displayProducts.length} מוצרים</span>
            {showCategories && <span>• {categoryResults.length} קטגוריות</span>}
            {hasActiveFilters && <span className="text-primary text-xs">(מסונן)</span>}
          </div>
        )}

        <div className="mt-2 flex-1 min-h-0 overflow-y-auto">
          {!isLoaded || isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Categories Section */}
              {showCategories && !isSearching && (
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">קטגוריות</h3>
                  <div className="flex flex-wrap gap-2">
                    <AnimatePresence mode="popLayout">
                      {categoryResults.slice(0, 6).map((collection, index) => (
                        <motion.div
                          key={collection.node.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.15, delay: index * 0.02 }}
                        >
                          <Link
                            to={`/collections/${collection.node.handle}`}
                            onClick={handleClose}
                            className="group flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-border/50 bg-background hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
                          >
                            {collection.node.image && (
                              <img
                                src={collection.node.image.url}
                                alt={collection.node.title}
                                loading="lazy"
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover"
                              />
                            )}
                            <span className="text-xs sm:text-sm group-hover:text-primary transition-colors">
                              {collection.node.title}
                            </span>
                            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                          </Link>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}

              {/* Products Section */}
              {hasResults && isSearching ? (
                // Show loading state while searching
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="gold-bar-loader" />
                  <p className="text-muted-foreground text-sm">מחפש...</p>
                </div>
              ) : displayExact.length > 0 ? (
                <div className="space-y-6">
                  {/* Exact matches */}
                  <div>
                    {hasResults && hasSearched && (
                      <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">
                        {displayExact.length === 1 ? 'נמצא מוצר אחד' : `נמצאו ${displayExact.length} מוצרים`} עבור "{searchQuery}"
                      </h3>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                      <AnimatePresence mode="popLayout">
                        {displayExact.map((product, index) => (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                          >
                            <Link
                              to={`/products/${product.handle}`}
                              onClick={handleClose}
                              className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="aspect-square bg-secondary/10 overflow-hidden">
                                {product.images?.edges?.[0]?.node ? (
                                  <img
                                    src={product.images.edges[0].node.url}
                                    alt={product.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                    <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="p-2 sm:p-3 text-right">
                                <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                  {product.title}
                                </h4>
                                <p className="text-primary font-medium text-xs sm:text-sm">
                                  {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Related products section - show when few exact matches */}
                  {showRelatedSection && (
                    <div className="border-t pt-4">
                      <h3 className="text-sm font-medium text-muted-foreground mb-2 sm:mb-3 text-right">
                        מוצרים קשורים שעשויים לעניין אותך
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                        <AnimatePresence mode="popLayout">
                          {displayRelated.slice(0, 8).map((product, index) => (
                            <motion.div
                              key={product.id}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              transition={{ duration: 0.2, delay: index * 0.03 }}
                            >
                              <Link
                                to={`/products/${product.handle}`}
                                onClick={handleClose}
                                className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                              >
                                <div className="aspect-square bg-secondary/10 overflow-hidden">
                                  {product.images?.edges?.[0]?.node ? (
                                    <img
                                      src={product.images.edges[0].node.url}
                                      alt={product.title}
                                      loading="lazy"
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                      <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                    </div>
                                  )}
                                </div>
                                <div className="p-2 sm:p-3 text-right">
                                  <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                    {product.title}
                                  </h4>
                                  <p className="text-primary font-medium text-xs sm:text-sm">
                                    {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                                  </p>
                                </div>
                              </Link>
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    </div>
                  )}
                </div>
              ) : hasResults && hasSearched && !isSearching && displayRelated.length > 0 ? (
                // No exact matches but have related products
                <div className="space-y-4">
                  <div className="text-center py-4 sm:py-6 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground text-sm sm:text-base">
                      לא נמצאו תוצאות מדויקות עבור "{searchQuery}"
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground/70 mt-1">
                      אבל יש לנו מוצרים קשורים{fallbackTerm ? ` ל"${fallbackTerm}"` : ''}:
                    </p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                    <AnimatePresence mode="popLayout">
                      {displayRelated.map((product, index) => (
                        <motion.div
                          key={product.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.2, delay: index * 0.03 }}
                        >
                          <Link
                            to={`/products/${product.handle}`}
                            onClick={handleClose}
                            className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                          >
                            <div className="aspect-square bg-secondary/10 overflow-hidden">
                              {product.images?.edges?.[0]?.node ? (
                                <img
                                  src={product.images.edges[0].node.url}
                                  alt={product.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                </div>
                              )}
                            </div>
                            <div className="p-2 sm:p-3 text-right">
                              <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                {product.title}
                              </h4>
                              <p className="text-primary font-medium text-xs sm:text-sm">
                                {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
                              </p>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              ) : hasResults && hasSearched && !isSearching ? (
                // No results at all
                <div className="text-center py-12 sm:py-16">
                  <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground text-sm sm:text-base">לא נמצאו תוצאות עבור "{searchQuery}"</p>
                  <p className="text-xs sm:text-sm text-muted-foreground/70 mt-2">נסה לחפש מילה אחרת</p>
                  {hasActiveFilters && (
                    <Button
                      variant="link"
                      onClick={clearFilters}
                      className="mt-3 text-primary"
                    >
                      נסה לנקות את הסינון
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">

                  {displayProducts.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
                      <AnimatePresence mode="popLayout">
                        {displayProducts.slice(0, 12).map((product, index) => (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                          >
                            <Link
                              to={`/products/${product.handle}`}
                              onClick={handleClose}
                              className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
                            >
                              <div className="aspect-square bg-secondary/10 overflow-hidden">
                                {product.images?.edges?.[0]?.node ? (
                                  <img
                                    src={product.images.edges[0].node.url}
                                    alt={product.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                    <Search className="h-6 w-6 sm:h-8 sm:w-8" />
                                  </div>
                                )}
                              </div>
                              <div className="p-2 sm:p-3 text-right">
                                <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                                  {product.title}
                                </h4>
                                <p className="text-primary font-medium text-xs sm:text-sm">
                                  {formatPrice(
                                    product.priceRange.minVariantPrice.amount,
                                    product.priceRange.minVariantPrice.currencyCode
                                  )}
                                </p>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
                      לא הצלחנו לטעון מוצרים כרגע
                    </div>
                  )}

                  <div className="text-center text-muted-foreground/80 text-xs sm:text-sm">
                    התחל להקליד כדי לחפש מוצרים
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
